import React, { useState } from "react";
import { IconStar } from "./Icons";

const Stars = ({ value = 5, max = 5 }) => (
  <span className="stars" aria-label={`${value} out of ${max}`}>
    {Array.from({ length: max }).map((_, i) => (
      <IconStar key={i} filled={i < Math.round(value)} />
    ))}
  </span>
);

const formatDate = (value) => {
  if (!value) return "";
  const [year, month] = value.split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
};

const ReviewCard = ({ review }) => {
  const [open, setOpen] = useState(false);
  const isLong = review.text.length > 320;
  const platformLabel = review.platform === "airbnb" ? "Airbnb" : "Booking.com";

  return (
    <article className="review">
      <div className="review__head">
        <div className="review__avatar" aria-hidden="true">
          {review.name.charAt(0).toUpperCase()}
        </div>
        <div>
          <div className="review__name">{review.name}</div>
          <div className="review__date">{formatDate(review.date)}</div>
        </div>
      </div>

      <Stars value={review.platform === "booking" ? review.rating / 2 : review.rating} />

      <p className={`review__text ${open ? "review__text--open" : ""}`}>{review.text}</p>

      {isLong && (
        <button type="button" className="review__toggle" onClick={() => setOpen((v) => !v)}>
          {open ? "Show less" : "Read full review"}
        </button>
      )}

      <div className="review__foot">
        <span className={`platform platform--${review.platform}`}>
          <span className="platform__dot" aria-hidden="true" />
          {platformLabel}
        </span>
        <span className="review__date">
          {review.platform === "booking" ? `${review.rating}/10` : `${review.rating}/5`}
        </span>
      </div>
    </article>
  );
};

export const Testimonials = ({ data }) => {
  const airbnb = data?.airbnb;
  const booking = data?.booking;
  const allReviews = data?.reviews || [];
  const [expanded, setExpanded] = useState(false);

  // Show a short, scannable set first — the full list is one click away.
  const INITIAL = 3;
  const reviews = expanded ? allReviews : allReviews.slice(0, INITIAL);
  const hasMore = allReviews.length > INITIAL;

  const generated = data?.generatedAt
    ? new Date(data.generatedAt).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : null;

  return (
    <section className="section" id="reviews">
      <div className="container">
        <div className="section-head">
          <span className="eyebrow">Guest reviews</span>
          <h2>What our guests say</h2>
          <hr className="rule" />
          <p>
            {airbnb?.count && booking?.count
              ? `${airbnb.count + booking.count} reviews across Airbnb and Booking.com — here are some of our favourites.`
              : "Ratings and reviews collected on Airbnb and Booking.com."}
          </p>
        </div>

        <div className="ratings">
          {airbnb?.score && (
            <a
              className="rating-card"
              href={airbnb.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Airbnb rating ${airbnb.score} out of 5`}
            >
              <div className="rating-card__score">
                {Number(airbnb.score).toFixed(2)}
                <small>/5</small>
              </div>
              <div className="rating-card__body">
                <div className="rating-card__label">Airbnb</div>
                <div className="rating-card__meta">
                  {airbnb.label} · {airbnb.count} reviews
                </div>
                <Stars value={airbnb.score} />
              </div>
            </a>
          )}

          {booking?.score && (
            <a
              className="rating-card"
              href={booking.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Booking.com rating ${booking.score} out of 10`}
            >
              <div className="rating-card__score">
                {Number(booking.score).toFixed(1)}
                <small>/10</small>
              </div>
              <div className="rating-card__body">
                <div className="rating-card__label">Booking.com</div>
                <div className="rating-card__meta">
                  {booking.label} · {booking.count} reviews
                </div>
                <Stars value={booking.score / 2} />
              </div>
            </a>
          )}
        </div>

        <div className="reviews__grid">
          {reviews.map((review) => (
            <ReviewCard key={review.id} review={review} />
          ))}
        </div>

        {hasMore && (
          <div className="reviews__more">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
            >
              {expanded
                ? "Show fewer reviews"
                : `Read all ${allReviews.length} guest reviews`}
            </button>
          </div>
        )}

        <div className="reviews__actions">
          {airbnb?.url && (
            <a
              className="btn btn--ghost btn--sm"
              href={`${airbnb.url}/reviews`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Airbnb reviews
            </a>
          )}
          {booking?.url && (
            <a
              className="btn btn--ghost btn--sm"
              href={`${booking.url}#tab-reviews`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Booking.com reviews
            </a>
          )}
        </div>

        {generated && (
          <p className="reviews__updated">
            Airbnb rating synced automatically on {generated}. Booking.com score is updated
            manually.
          </p>
        )}
      </div>
    </section>
  );
};

export default Testimonials;
